import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';

import Heading from 'shopper/components/Heading';

import ErrorBoundary from 'components/ErrorBoundary';
import FlatLayout from 'components/Layout/FlatLayout';
import PageDescription from 'components/PageDescription';
import FaqJsonLd from 'components/Seo/FaqJsonLd';

import { getStoreCouponsMeta } from 'lib/couponPage';
import getDefaultFaq from 'lib/faq/getDefaultFaq';
import parseFaqFromHtml from 'lib/faq/parseFaqFromHtml';
import parseFaqFromHtmlV2, {
  stripFaqBlocksV2,
} from 'lib/faq/parseFaqFromHtmlV2';
import { trackEvent } from 'lib/posthog';
import { getStoreOffersMeta, selectFaqHtml } from 'lib/storePage';
import {
  extractContactSignals,
  htmlToPlainText,
  parseStoreContentSections,
  stripDuplicateLeadingHeading,
} from 'lib/storePage/parseStoreContentSections';
import { sendSupixEvent } from 'lib/supix';
import reportCls from 'lib/webVitals/reportCls';

import { useUtm } from 'providers/UtmProvider';

import Breadcrumb from './Breadcrumb';
import CouponVerificationCard from './CouponVerificationCard';
import FaqSection from './FaqSection';
import Head from './Head';
import Schema from './Schema';
import Sidebar from './Sidebar';
import StoreSummary from './StoreSummary';
import TimelineList from './TimelineList';

import CLS_REGIONS from 'constants/clsRegions';
import COUPONS from 'constants/coupons';

const StoreAd = dynamic(() => import('./StoreAd'));

const Store = ({
  gaEventCategory = 'offers_store',
  isExclusivelyCoupon = false,
  isStoreChecklistActive = true,
  serverCoupons,
  serverDate,
  serverDateFormatted,
  serverMonth,
  serverYear,
  serverOffers,
  store: {
    couponPageDescription,
    googleIndex,
    hasPhysicalStore,
    isStoreCnpjValid,
    isStoreVerified,
    offerPageDescription,
    offersMetaDescription,
    storeAddress,
    storeContacts,
    storeDescription,
    storeDomain,
    storeDomainEnriched,
    storeFacebookPage,
    storeId,
    storeImage,
    storeName,
    storeRating,
    storeRatingCount,
    storeSlug,
  },
  storeAd,
  fixedOffer = null,
}) => {
  const { isUtmLoaded } = useUtm();

  const isOfferPage = !isExclusivelyCoupon && !!serverOffers;

  const activeCouponCount = useMemo(
    () =>
      !isOfferPage
        ? serverCoupons?.coupons?.filter(
            // API only returns APPROVED and FINISHED for this endpoint;
            // !FINISHED is equivalent to APPROVED in practice.
            (c) => c.couponStatusName !== COUPONS.STATUS.FINISHED
          )?.length
        : 0,
    [isOfferPage, serverCoupons?.coupons]
  );

  useEffect(() => {
    if (isUtmLoaded) {
      const endpoint = isOfferPage ? '/st' : '/cp';

      sendSupixEvent(endpoint, {
        id: storeId,
      });
    }
  }, [storeId, isOfferPage, isUtmLoaded]);

  useEffect(() => {
    trackEvent(
      isOfferPage ? 'view store offers page' : 'view coupon store page',
      {
        type: 'screen',
        source_type: 'navigation',
      }
    );
  }, [storeSlug, isOfferPage]);

  // Diagnostic instrumentation for the mobile-only CLS on coupon store pages.
  // Offer pages share this component but do not show the regression, so they
  // are excluded to keep the sampled event volume down.
  useEffect(() => {
    if (isOfferPage) return;

    reportCls();
  }, [isOfferPage]);

  const isStoreAdVisible = !!storeAd?.image;
  const { title: storeCouponTitle, description: storeCouponDescription } =
    getStoreCouponsMeta({
      activeCoupons: serverCoupons?.coupons || [],
      storeName,
    });

  const {
    title: storeOffersTitle,
    description: storeOffersDescription,
    h1Text: storeOffersH1,
  } = getStoreOffersMeta({
    storeName,
    serverMonth,
    serverYear,
    offersMetaDescription,
  });

  const dynamicMetaTitle = isOfferPage ? storeOffersTitle : storeCouponTitle;

  const dynamicMetaDescription = isOfferPage
    ? storeOffersDescription
    : storeCouponDescription;

  // Editorial source per page type. Decomposed into named sections (FAQ,
  // Contato, Sobre, Outras informações) so each can feed a different SEO
  // surface. Stores without the section convention degrade gracefully — the
  // entire blob falls into `outras` and renders as before.
  const sourceHtml = isOfferPage ? offerPageDescription : couponPageDescription;

  const sections = useMemo(
    () => parseStoreContentSections(sourceHtml),
    [sourceHtml]
  );

  // True when at least one recognized <h2> heading was found (FAQ, Contato,
  // Sobre, or Outras informações). Sourced from the parser to differentiate
  // "user wrote <h2>Outras informações</h2>" (recognized → don't fall back to
  // blob for FAQ) from "free-form legacy content" (unrecognized → blob OK).
  const hasNamedSections = sections.hasRecognizedSection;

  // Coupons keep getDefaultFaq fallback; offers have none.
  const faqItems = useMemo(() => {
    const faqHtml = selectFaqHtml({ sections, sourceHtml, hasNamedSections });

    if (faqHtml === null) {
      return isOfferPage ? [] : getDefaultFaq(storeName);
    }

    if (isOfferPage) {
      return parseFaqFromHtmlV2(faqHtml);
    }

    const parsed = parseFaqFromHtml(faqHtml);
    return parsed.length > 0 ? parsed : getDefaultFaq(storeName);
  }, [sections, sourceHtml, hasNamedSections, isOfferPage, storeName]);

  // Contact signals (email, support URL, social profiles) feed
  // Store.contactPoint and Organization.sameAs in Schema. Pass storeDomain
  // so customerServiceUrl is restricted to the store's own host.
  const contactSignals = useMemo(
    () => extractContactSignals(sections.contato, { storeDomain }),
    [sections.contato, storeDomain]
  );

  // First ~500 chars of the Sobre section feed Store.description in Schema.
  // Truncate at the last word boundary to avoid cutting mid-word.
  const storeDescriptionText = useMemo(() => {
    if (!sections.sobre) return null;
    const plain = htmlToPlainText(sections.sobre);
    if (plain.length <= 500) return plain.trim() || null;
    const truncated = plain.slice(0, 500).replace(/\s+\S*$/, '');
    return truncated.trim() || null;
  }, [sections.sobre]);

  // Strip the leading <h2>/<h3> from the Sobre section when it duplicates the
  // template-rendered heading "Sobre {storeName}".
  const sobreHtml = useMemo(
    () => stripDuplicateLeadingHeading(sections.sobre, `Sobre ${storeName}`),
    [sections.sobre, storeName]
  );

  // Legacy-blob fallback when no named sections were recognized. Sectioned
  // content is rendered per-bucket below to keep semantic structure correct
  // (each section under its own heading; outras is NOT nested under Sobre).
  const legacyPageDescription = useMemo(() => {
    if (hasNamedSections) return null;
    if (!sourceHtml || typeof sourceHtml !== 'string') return null;

    if (isOfferPage) {
      return stripFaqBlocksV2(sourceHtml).trim() || null;
    }

    return (
      sourceHtml
        .replace(/<h3[^>]*>.*?<\/h3>\s*<p[^>]*>.*?<\/p>/gis, '')
        .trim() || null
    );
  }, [hasNamedSections, sourceHtml, isOfferPage]);

  const hasSectionedBody = !!(sobreHtml || sections.outras || sections.contato);

  const shouldRenderBody = hasNamedSections
    ? hasSectionedBody
    : !!legacyPageDescription && (isOfferPage || faqItems.length === 0);

  return (
    <>
      <FlatLayout.Margin className="gap-8 lg:gap-12">
        <FlatLayout.Heading>
          <Head
            googleIndex={googleIndex}
            metaDescription={dynamicMetaDescription}
            metaTitle={dynamicMetaTitle}
            // ≤5 active coupons = "thin" page per RF-12; threshold avoids
            // indexing near-empty store pages that hurt crawl quality.
            // undefined guard prevents false noindex on transient SSR errors.
            noindex={
              !isOfferPage &&
              activeCouponCount !== undefined &&
              activeCouponCount <= 5
            }
          />
          <Schema
            contactSignals={contactSignals}
            isOfferPage={isOfferPage}
            serverDate={serverDate}
            storeDescriptionText={storeDescriptionText}
            storeDomain={storeDomain}
            storeImage={storeImage}
            storeName={storeName}
            storeRating={storeRating}
            storeRatingCount={storeRatingCount}
            storeSlug={storeSlug}
          />
          <Breadcrumb isOfferPage={isOfferPage} storeName={storeName} />
          <StoreSummary
            h1Text={isOfferPage ? storeOffersH1 : null}
            isExclusivelyCoupon={isExclusivelyCoupon}
            isStoreVerified={isStoreVerified}
            serverDateFormatted={serverDateFormatted}
            serverMonth={serverMonth}
            serverYear={serverYear}
            storeDomain={storeDomain}
            storeImage={storeImage}
            storeName={storeName}
          />
        </FlatLayout.Heading>
        <FlatLayout.Body>
          {isStoreAdVisible && (
            <ErrorBoundary>
              <StoreAd
                className="mb-6"
                gaEventCategory={gaEventCategory}
                storeName={storeName}
                {...storeAd}
              />
            </ErrorBoundary>
          )}
          <TimelineList
            fixedOffer={fixedOffer}
            gaEventCategory={gaEventCategory}
            isOfferPage={isOfferPage}
            serverCoupons={serverCoupons}
            serverOffers={serverOffers}
            storeId={storeId}
            storeName={storeName}
            storeSlug={storeSlug}
          />
          {!isOfferPage && (
            <div
              className="mt-12 lg:mt-16"
              data-cls-region={CLS_REGIONS.VERIFICATION_CARD}
            >
              <CouponVerificationCard />
            </div>
          )}
          {faqItems.length > 0 && (
            <>
              <FaqJsonLd faqItems={faqItems} />
              <ErrorBoundary>
                <FaqSection
                  className="mt-12 lg:mt-16"
                  faqItems={faqItems}
                  storeName={storeName}
                />
              </ErrorBoundary>
            </>
          )}
          {/* Em offer pages, conteúdo institucional ("Sobre", "Marketplace",
           * "Black Friday", etc.) renderiza mesmo com FAQ presente.
           * shouldRenderBody=true quando há qualquer seção visível (sobre,
           * outras, contato) ou conteúdo legado.
           *
           * Em cupom, mantém o gate `faqItems.length === 0` quando legado
           * porque couponPageDescription historicamente não carrega
           * conteúdo institucional além das FAQs. */}
          {shouldRenderBody && (
            <ErrorBoundary>
              {hasNamedSections ? (
                <>
                  {sobreHtml && (
                    <section
                      aria-label={`Sobre ${storeName}`}
                      className="mt-12 flex flex-col gap-10 lg:mt-16"
                      // Shares a target selector with FaqSection, so without
                      // this marker the two cannot be told apart in the field
                      data-cls-region={CLS_REGIONS.ABOUT}
                    >
                      <Heading
                        as="h2"
                        className="border-b border-neutral-high-400 pb-6 text-2xl font-semibold text-neutral-low-400 dark:border-neutral-low-200 dark:text-neutral-high-200"
                        size="size4"
                      >
                        {`Sobre ${storeName}`}
                      </Heading>
                      <PageDescription pageDescription={sobreHtml} />
                    </section>
                  )}
                  {sections.outras && (
                    <PageDescription
                      className={sobreHtml ? 'mt-12' : 'mt-18'}
                      pageDescription={sections.outras}
                    />
                  )}
                  {sections.contato && (
                    <section
                      aria-label="Contato"
                      className="mt-12 flex flex-col gap-10"
                    >
                      <Heading
                        as="h2"
                        className="border-b border-neutral-high-400 pb-6 text-2xl font-semibold text-neutral-low-400 dark:border-neutral-low-200 dark:text-neutral-high-200"
                        size="size4"
                      >
                        Contato
                      </Heading>
                      <PageDescription pageDescription={sections.contato} />
                    </section>
                  )}
                </>
              ) : (
                <PageDescription
                  className="mt-18"
                  pageDescription={legacyPageDescription}
                />
              )}
            </ErrorBoundary>
          )}
        </FlatLayout.Body>
        <Sidebar
          gaEventCategory={gaEventCategory}
          hasPhysicalStore={hasPhysicalStore}
          isCnpjValid={isStoreCnpjValid}
          isOfferPage={isOfferPage}
          isStoreChecklistActive={isStoreChecklistActive}
          isVerified={isStoreVerified}
          storeAddress={storeAddress}
          storeContacts={storeContacts}
          storeDescription={storeDescription}
          storeDomain={storeDomain}
          storeDomainEnriched={storeDomainEnriched}
          storeFacebookPage={storeFacebookPage}
          storeName={storeName}
          storeRating={storeRating}
          storeRatingCount={storeRatingCount}
          storeSlug={storeSlug}
        />
        <FlatLayout.ScrollToTop gaEventCategory={gaEventCategory} />
      </FlatLayout.Margin>
      <FlatLayout.Footer />
    </>
  );
};

Store.propTypes = {
  fixedOffer: PropTypes.object,
  gaEventCategory: PropTypes.string,
  isExclusivelyCoupon: PropTypes.bool,
  isStoreChecklistActive: PropTypes.bool,
  serverCoupons: PropTypes.shape({
    after: PropTypes.string,
    coupons: PropTypes.arrayOf(
      PropTypes.shape({
        couponId: PropTypes.number.isRequired,
        couponDiscountValue: PropTypes.string.isRequired,
        couponDiscountShort: PropTypes.string.isRequired,
        couponDiscount: PropTypes.string.isRequired,
        couponDiscountOn: PropTypes.string.isRequired,
        couponStatusName: PropTypes.string.isRequired,
        couponInstructions: PropTypes.string.isRequired,
        couponUntil: PropTypes.string,
        couponVerified: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
        storeImage: PropTypes.string,
        storeName: PropTypes.string.isRequired,
        storeDomain: PropTypes.string.isRequired,
        storeSlug: PropTypes.string.isRequired,
        couponTag: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
        couponPublished: PropTypes.string.isRequired,
        couponTitle: PropTypes.string.isRequired,
        couponCode: PropTypes.string.isRequired,
        couponUrl: PropTypes.string.isRequired,
        userName: PropTypes.string,
      })
    ).isRequired,
  }),
  serverOffers: PropTypes.shape({
    after: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sort: PropTypes.string,
    before: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    offers: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        offerComments: PropTypes.number.isRequired,
        offerId: PropTypes.number.isRequired,
        offerIsHighlight: PropTypes.bool.isRequired,
        ratings: PropTypes.shape().isRequired,
        offerOldPrice: PropTypes.number,
        offerPhoto: PropTypes.string.isRequired,
        offerPrice: PropTypes.number.isRequired,
        offerPriceType: PropTypes.string.isRequired,
        offerPublished: PropTypes.string.isRequired,
        offerSlug: PropTypes.string.isRequired,
        offerStatusName: PropTypes.string.isRequired,
        offerUserVisibility: PropTypes.string.isRequired,
        offerTags: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
          })
        ),
        offerTitle: PropTypes.string.isRequired,
        storeDomain: PropTypes.string.isRequired,
      }).isRequired
    ).isRequired,
  }),
  store: PropTypes.shape({
    storeId: PropTypes.number.isRequired,
    storeName: PropTypes.string.isRequired,
    couponPageDescription: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.array,
    ]),
    offerPageDescription: PropTypes.string,
    storeDescription: PropTypes.string,
    pageCouponsTitle: PropTypes.string,
    pageOffersTitle: PropTypes.string,
    offersMetaDescription: PropTypes.string,
    storeImage: PropTypes.string,
    storeSlug: PropTypes.string.isRequired,
    storeDomain: PropTypes.string.isRequired,
    storeDomainEnriched: PropTypes.string,
    storeRating: PropTypes.number,
    storeRatingCount: PropTypes.number,
    storeContacts: PropTypes.string,
    storeAddress: PropTypes.string,
    storeFacebookPage: PropTypes.string,
    googleIndex: PropTypes.bool,
    isStoreIndexed: PropTypes.bool.isRequired,
    isStoreVerified: PropTypes.bool.isRequired,
    isStoreCnpjValid: PropTypes.bool.isRequired,
    hasPhysicalStore: PropTypes.bool.isRequired,
  }).isRequired,
  storeAd: PropTypes.shape({
    description: PropTypes.string.isRequired,
    gaEventCategory: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    storeName: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
};

export default Store;
